import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { GraduationCap, BookOpen, FileText, Calendar, TrendingUp, Clock, CheckCircle, AlertCircle, Video, ChevronRight } from 'lucide-react';
import axios from 'axios';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalUnits: 0,
    completedUnits: 0,
    activeUnits: 0,
    pendingAssignments: 0
  });
  const [studentData, setStudentData] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);
  const [upcomingClasses, setUpcomingClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // First, get student details
      const studentResponse = await axios.get(`/students/${user.id}?includeCourse=true&includeCampus=true`);
      const studentData = studentResponse.data;
      setStudentData(studentData);

      const isSelfPaced = studentData.institution?.learningMode === 'self_paced';

      if (!studentData.courseId && !isSelfPaced) {
        console.log('Student has no courseId and is not self-paced');
        setStats({ totalUnits: 0, completedUnits: 0, activeUnits: 0, pendingAssignments: 0 });
        setRecentActivities([]);
        setUpcomingClasses([]);
        return;
      }

      // Fetch all units for the student's course with proper campus and assignment filtering
      const courseUnitsResponse = await axios.get(`/enrollments/available-courses?studentId=${user.id}`);
      const coursesData = courseUnitsResponse.data;

      // Fetch student's enrolled units
      const studentUnitsResponse = await axios.get('/student-units');
      const studentUnitsData = studentUnitsResponse.data;
      const studentUnitMap = new Map(studentUnitsData.map(su => [su.unitId, su]));

      // Merge courses and units (cost is already included from backend available-courses)
      const mergedUnits = coursesData.flatMap(course =>
        course.Units.map(unit => {
          const studentUnit = studentUnitMap.get(unit.id);
          const isAccepted = unit.status === 'active' || unit.status === 'completed';
          return {
            ...unit,
            Course: { id: course.id, name: course.name, description: course.description },
            cost: unit.cost || null,
            currency: unit.currency || 'KES',
            originalStatus: unit.status,
            enrollmentStatus: studentUnit ? studentUnit.status : 'not_enrolled',
            paymentStatus: studentUnit ? studentUnit.paymentStatus : 'not_enrolled',
            studentUnitId: studentUnit ? studentUnit.id : null,
            lecturer: isAccepted ? unit.Lecturer : null,
            classDate: isAccepted ? unit.classDate : null,
          };
        })
      );

      // Calculate stats from merged units
      const stats = {
        totalUnits: mergedUnits.length,
        completedUnits: mergedUnits.filter(unit => unit.enrollmentStatus === 'completed').length,
        activeUnits: mergedUnits.filter(unit => unit.enrollmentStatus === 'active' || unit.enrollmentStatus === 'in-progress').length,
        pendingAssignments: mergedUnits.filter(unit => unit.paymentStatus === 'pending').length
      };

      // Get recent activities from dashboard endpoint
      const dashboardResponse = await axios.get(`/students/${user.id}/dashboard`);
      const dashboardData = dashboardResponse.data;

      // Set data
      setStats(stats);
      setRecentActivities(dashboardData.recentActivities || []);
      setUpcomingClasses(dashboardData.upcomingClasses || []);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load dashboard data. Please try again.');

      // Fallback to mock data if API fails
      setStats({
        totalUnits: 0,
        completedUnits: 0,
        activeUnits: 0,
        pendingAssignments: 0
      });
      setRecentActivities([]);
      setUpcomingClasses([]);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color, bgColor }) => (
    <div className={`card animate-fade-in`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className="text-3xl font-bold text-gray-900">{value}</p>
          {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-full ${bgColor}`}>
          <Icon className={`w-6 h-6 ${color}`} />
        </div>
      </div>
    </div>
  );

  const ActivityItem = ({ activity }) => {
    const getActivityIcon = () => {
      switch (activity.type) {
        case 'assignment':
          return <FileText className="w-5 h-5 text-blue-600" />;
        case 'quiz':
          return <CheckCircle className="w-5 h-5 text-green-600" />;
        case 'material':
          return <BookOpen className="w-5 h-5 text-purple-600" />;
        default:
          return <Clock className="w-5 h-5 text-gray-600" />;
      }
    };

    const getStatusBadge = () => {
      if (activity.status === 'completed') {
        return <span className="status-badge status-active">Completed</span>;
      } else if (activity.status === 'pending') {
        return <span className="status-badge status-pending">Pending</span>;
      }
      return null;
    };

    return (
      <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
        <div className="flex-shrink-0">
          {getActivityIcon()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-900 truncate">
              {activity.title}
            </p>
            {getStatusBadge()}
          </div>
          {activity.course && (
            <p className="text-sm text-gray-600">{activity.course}</p>
          )}
          {activity.score && (
            <p className="text-sm text-green-600 font-medium">Score: {activity.score}</p>
          )}
          {activity.dueDate && (
            <p className="text-sm text-red-600">Due: {new Date(activity.dueDate).toLocaleDateString()}</p>
          )}
          <p className="text-xs text-gray-500 mt-1">{activity.timeAgo}</p>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">
          Welcome back, {user?.name || 'Student'}! 👋
        </h1>
        <p className="text-primary-100">
          Here's what's happening in your learning journey today.
        </p>

        {/* Course Completion Status */}
        {studentData?.courseStatus && (
          <div className="mt-4">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${studentData.courseStatus === 'completed'
              ? 'bg-green-100 text-green-800'
              : studentData.courseStatus === 'in-progress'
                ? 'bg-blue-100 text-blue-800'
                : 'bg-yellow-100 text-yellow-800'
              }`}>
              {studentData.courseStatus === 'completed' && '🎉 '}
              {studentData.courseStatus === 'in-progress' && '📚 '}
              {studentData.courseStatus === 'enrolled' && '📝 '}
              Course Status: {
                studentData.courseStatus === 'completed' ? 'Completed' :
                  studentData.courseStatus === 'in-progress' ? 'In Progress' :
                    'Enrolled'
              }
            </span>
          </div>
        )}
      </div>

      {/* Self-Paced Portal Banner */}
      {studentData?.institution?.learningMode === 'self_paced' && (
        <div className="bg-gradient-to-r from-indigo-600 to-primary-700 rounded-xl p-6 text-white shadow-lg relative overflow-hidden animate-fade-in mb-6">
          <div className="absolute right-0 top-0 opacity-10 transform translate-x-1/4 -translate-y-1/4">
            <BookOpen className="w-64 h-64" />
          </div>
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <span className="px-2 py-0.5 bg-indigo-500 bg-opacity-50 rounded-full text-xs font-bold uppercase tracking-wider border border-indigo-400">
                  Self-Paced Portal
                </span>
              </div>
              <h2 className="text-3xl font-bold mb-2">Explore Your Learning Path</h2>
              <p className="text-primary-100 max-w-2xl text-lg">
                Discover all available courses and subjects. In self-paced mode, you have the freedom to choose your own subjects and learn at your own speed.
              </p>
            </div>
            <button
              onClick={() => navigate('/units')}
              className="px-8 py-3 bg-white text-indigo-700 rounded-xl font-bold hover:bg-indigo-50 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:translate-y-0 whitespace-nowrap flex items-center space-x-2"
            >
              <span>Browse Catalog</span>
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Units"
          value={stats.totalUnits}
          subtitle="Enrolled this semester"
          icon={BookOpen}
          color="text-blue-600"
          bgColor="bg-blue-100"
        />
        <StatCard
          title="Completed"
          value={stats.completedUnits}
          subtitle="Units finished"
          icon={CheckCircle}
          color="text-green-600"
          bgColor="bg-green-100"
        />
        <StatCard
          title="Active Units"
          value={stats.activeUnits}
          subtitle="Currently studying"
          icon={GraduationCap}
          color="text-purple-600"
          bgColor="bg-purple-100"
        />
        <StatCard
          title="Pending Tasks"
          value={stats.pendingAssignments}
          subtitle="Assignments & quizzes"
          icon={AlertCircle}
          color="text-orange-600"
          bgColor="bg-orange-100"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activities */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Recent Activities</h3>
            <p className="card-subtitle">Your latest academic activities</p>
          </div>
          <div className="space-y-3">
            {recentActivities.map((activity) => (
              <ActivityItem key={activity.id} activity={activity} />
            ))}
          </div>
          {recentActivities.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Clock className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No recent activities</p>
            </div>
          )}
        </div>

        {/* Next Lecture or Continue Learning */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">{studentData?.course?.selfPacedEnabled ? 'Continue Learning' : 'Next Lecture'}</h3>
            <p className="card-subtitle">{studentData?.course?.selfPacedEnabled ? 'Pick up where you left off' : 'Your immediate upcoming class'}</p>
          </div>

          {studentData?.course?.selfPacedEnabled ? (
            /* Self-Paced View */
            (() => {
              // Find next unit to study
              // Logic: First 'in-progress', else first 'active' (enrolled), else first 'upcoming' (available)
              // We need access to mergedUnits here inside the render.
              // Since mergedUnits is local to fetchDashboardData, we should probably store it in state or re-derive.
              // For now, let's use a workaround: The stats card uses aggregated data. 
              // We might need to store 'nextUnit' in state. 
              // A better approach is to leverage the existing 'upcomingClasses' state or add a new 'nextUnit' state.
              // Let's assume we add 'nextUnit' to state in a separate edit, or simplifying:
              // We don't have the units list in rendering scope easily without state.
              return (
                <div className="p-6 text-center">
                  <BookOpen className="w-12 h-12 mx-auto mb-4 text-blue-300" />
                  <p className="text-gray-600 mb-4">Go to your units page to continue your self-paced learning journey.</p>
                  <button
                    onClick={() => navigate('/units')}
                    className="btn-primary"
                  >
                    Go to My Units
                  </button>
                </div>
              );
            })()
          ) : (
            /* Instructor-Led View */
            upcomingClasses.length > 0 ? (
              <div className="p-6 bg-gradient-to-r from-primary-50 to-primary-100 rounded-lg border border-primary-200">
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-lg font-semibold text-gray-900 mb-1">
                      {upcomingClasses[0].title}
                    </h4>
                    <p className="text-sm text-gray-600 mb-2">
                      {studentData?.institution?.learningMode === 'self_paced' ? 'Tutor' : 'Lecturer'}: {upcomingClasses[0].lecturer}
                    </p>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>{new Date(upcomingClasses[0].date).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4" />
                        <span>{upcomingClasses[0].time}</span>
                      </div>
                    </div>
                    {upcomingClasses[0].location && (
                      <p className="text-sm text-gray-500 mt-1">
                        📍 {upcomingClasses[0].location}
                      </p>
                    )}
                  </div>
                </div>
                {upcomingClasses.length > 1 && (
                  <div className="mt-4 pt-4 border-t border-primary-200">
                    <p className="text-xs text-primary-600">
                      +{upcomingClasses.length - 1} more class{upcomingClasses.length - 1 > 1 ? 'es' : ''} scheduled
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No upcoming {studentData?.institution?.learningMode === 'self_paced' ? 'classes' : 'lectures'} scheduled</p>
                <p className="text-sm text-gray-400 mt-1">Check back later for your schedule</p>
              </div>
            ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Quick Actions</h3>
          <p className="card-subtitle">Common tasks and shortcuts</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {!studentData?.course?.selfPacedEnabled && (
            <button
              onClick={() => navigate('/virtual-classroom')}
              className="flex flex-col items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <Video className="w-8 h-8 text-red-600 mb-2" />
              <span className="text-sm font-medium text-gray-900">Virtual Class</span>
            </button>
          )}
          <button
            onClick={() => navigate('/units')}
            className="flex flex-col items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <BookOpen className="w-8 h-8 text-blue-600 mb-2" />
            <span className="text-sm font-medium text-gray-900">View Units</span>
          </button>
          <button
            onClick={() => navigate('/assignments')}
            className="flex flex-col items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <FileText className="w-8 h-8 text-green-600 mb-2" />
            <span className="text-sm font-medium text-gray-900">Assignments</span>
          </button>
          <button
            onClick={() => navigate('/learning-materials')}
            className="flex flex-col items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <GraduationCap className="w-8 h-8 text-purple-600 mb-2" />
            <span className="text-sm font-medium text-gray-900">Materials</span>
          </button>
          <button
            onClick={() => navigate('/assignments')}
            className="flex flex-col items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <Calendar className="w-8 h-8 text-orange-600 mb-2" />
            <span className="text-sm font-medium text-gray-900">Schedule</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
